import type { AuthResult } from './types';

const BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api/v1';
const STORAGE_KEY = 'mikromanager.session';

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
}

/** Cuerpo de error del backend (ver `AllExceptionsFilter`). */
interface ErrorBody {
  statusCode?: number;
  error?: string;
  message?: string | string[];
  routerDetail?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Presente cuando el fallo viene del router y no de MikroManager. */
    readonly routerDetail?: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---------------------------------------------------------------- sesión

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return parsed.accessToken && parsed.refreshToken ? parsed : null;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession | null): void {
  if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  else localStorage.removeItem(STORAGE_KEY);
}

/** Suscriptores a "la sesión dejó de ser válida"; el AuthProvider redirige al login. */
const expiryListeners = new Set<() => void>();

export function onSessionExpired(listener: () => void): () => void {
  expiryListeners.add(listener);
  return () => expiryListeners.delete(listener);
}

function notifyExpired(): void {
  saveSession(null);
  for (const listener of expiryListeners) listener();
}

// ---------------------------------------------------------------- refresh

/**
 * Renovación con una sola petición en vuelo.
 *
 * Al cargar el panel se disparan varias consultas a la vez; si el access token acaba de
 * caducar, todas verían un 401 al mismo tiempo. Sin esta promesa compartida cada una
 * intentaría renovar, y como el backend **rota** el refresh token, la primera invalidaría
 * el de las demás y la sesión se caería sola.
 */
let refreshInFlight: Promise<StoredSession | null> | null = null;

async function refreshSession(): Promise<StoredSession | null> {
  refreshInFlight ??= (async () => {
    const current = loadSession();
    if (!current) return null;

    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });

      if (!response.ok) return null;

      const result = (await response.json()) as AuthResult;
      const next: StoredSession = {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      };
      saveSession(next);
      return next;
    } catch {
      return null;
    } finally {
      // Se libera en el siguiente tick para que las peticiones que ya estaban esperando
      // resuelvan con esta misma renovación y no arranquen otra.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

// ---------------------------------------------------------------- fetch

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Uso interno: evita reintentar en bucle tras renovar. */
  skipRefresh?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ErrorBody = {};
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    // Un 502 de un proxy puede devolver HTML; no es motivo para perder el status.
  }

  const message = Array.isArray(body.message)
    ? body.message.join('. ')
    : (body.message ?? `Error ${response.status}`);

  return new ApiError(response.status, message, body.routerDetail, body.error);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const session = loadSession();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (session) headers['Authorization'] = `Bearer ${session.accessToken}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 401 && session && !options.skipRefresh) {
    const renewed = await refreshSession();
    if (!renewed) {
      notifyExpired();
      throw new ApiError(401, 'La sesión caducó. Vuelve a iniciar sesión.');
    }
    return apiRequest<T>(path, { ...options, skipRefresh: true });
  }

  if (response.status === 401) {
    notifyExpired();
    throw await parseError(response);
  }

  if (!response.ok) throw await parseError(response);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Descarga autenticada de un archivo (exportación de vouchers). */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const session = loadSession();
  const response = await fetch(buildUrl(path), {
    headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
  });

  if (!response.ok) throw await parseError(response);

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const blob = await response.blob();

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = match?.[1] ?? fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => apiRequest<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};
